import CountrySelect from "@/components/address/CountrySelect";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import {
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { fillFormBirthDateFromPin, ibanNormalizeFunction, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { ALL_WHITE_SPACES_PATTERN } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { AutoComplete, Checkbox, Col, DatePicker, Divider, Form, Input, Row, Select } from "antd";
import { FormInstance } from "antd/lib/form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { useGetAgentEnumerations } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { Country } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import { UUID } from "../../../../../typings/global";
import {
  useCurrentAgentAccount,
  useHasAnyPermission,
  useHasPermissions,
  useIsCurrentAgentTypePlus,
} from "../../../../auth/hooks";
import AcademicDegreeAfterSelect from "../../../../enumerations/components/form/AcademicDegreeAfterSelect";
import AcademicDegreeSelect from "../../../../enumerations/components/form/AcademicDegreeSelect";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { ClientIdentityCardType } from "../../../enums";
import { CreateUpdateNaturalClient } from "../../../types";

interface Props {
  form: FormInstance<CreateUpdateNaturalClient>;
  colSpan: number;
  disablePinInput?: boolean;
  requireIdCardNumber?: boolean;
  previousIdCardNumber?: string;
  onlyIdCardIdentityTypeSupport?: boolean;
  clientManagedById?: UUID;
  isCreatingNew?: boolean;
}

export const NaturalClientDataFormPart = ({
  form,
  colSpan,
  disablePinInput,
  requireIdCardNumber,
  previousIdCardNumber,
  onlyIdCardIdentityTypeSupport,
  clientManagedById,
  isCreatingNew,
}: Props) => {
  const { t } = useTranslation();

  const currentAgent = useCurrentAgentAccount()?.agent;
  const hasManagedByChangePermission = useHasAnyPermission(
    Permission.CLIENT_MANAGED_BY_CHANGE,
    Permission.PRIVILEGED_CLIENT_MANAGED_BY_CHANGE,
  );
  const hasPrivilegedManagedByChangePermission = useHasPermissions(Permission.PRIVILEGED_CLIENT_MANAGED_BY_CHANGE);
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const identityCardType = Form.useWatch("identityCardType", form);
  const birthDate = Form.useWatch("birthDate", form);
  const birthYear = birthDate ? dayjs(birthDate).year() : undefined;

  const { data: defaultAgent } = useGetAgentEnumerations(clientManagedById ? [clientManagedById] : []);

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="managedById"
            label={
              <LabelWithTooltip tooltip={t("client.helpers.managedByHint")} label={t("client.attrs.managedById")} />
            }
            rules={[validations.notNull]}
            initialValue={isTopAgentPlus ? undefined : currentAgent?.id}
          >
            <AgentSelect
              optionsProps={{
                subordinateTreePath:
                  isCreatingNew || hasPrivilegedManagedByChangePermission ? undefined : currentAgent?.accessTreePath,
                defaultOption: defaultAgent?.[0],
              }}
              disabled={!isCreatingNew && !hasManagedByChangePermission}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <AcademicDegreeSelect
            formItemProps={{ name: "academicDegree", label: t("client.attrs.academicDegree") }}
            selectProps={{ allowClear: true }}
          />
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="firstName"
            label={t("client.attrs.firstName")}
            rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="lastName"
            label={t("client.attrs.lastName")}
            rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <AcademicDegreeAfterSelect
            formItemProps={{ name: "academicDegreeAfter", label: t("client.attrs.academicDegreeAfter") }}
            selectProps={{ allowClear: true }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="personalIdentificationNumber"
            label={
              <LabelWithTooltip
                tooltip={t("helpers.pinFormat")}
                label={t("client.attrs.personalIdentificationNumberLong")}
              />
            }
            rules={[validations.notBlank, validations.pin]}
          >
            <Input
              disabled={disablePinInput}
              onChange={(event) => fillFormBirthDateFromPin(event.target.value, form)}
            />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="birthDate"
            label={t("client.attrs.birthDate")}
            rules={[validations.notNull, validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} showNow={false} maxDate={dayjs().subtract(1, "day")} />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item name="birthPlace" label={t("client.attrs.birthPlace")} rules={[validations.size(1, 64)]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="identityCardType"
            label={t("client.enums.clientIdentityCardType._label")}
            rules={[validations.notNull]}
            initialValue={ClientIdentityCardType.ID_CARD}
          >
            <Select
              {...selectStandardProps}
              options={Object.values(ClientIdentityCardType)
                .filter((type) => !onlyIdCardIdentityTypeSupport || type === ClientIdentityCardType.ID_CARD)
                .map((type) => ({
                  label: t(`client.enums.clientIdentityCardType.${type}`),
                  value: type,
                }))}
            />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="identityCardNumber"
            label={t("client.attrs.identityCardNumber")}
            rules={[
              requireIdCardNumber ? validations.notBlank : validations.none,
              identityCardType === ClientIdentityCardType.ID_CARD
                ? validations.pattern(regexPatterns.idCardRegex)
                : validations.size(6, 12),
            ]}
            dependencies={["identityCardType"]}
            normalize={upperCaseStringNormalizeFunction}
          >
            {previousIdCardNumber ? (
              <AutoComplete
                options={[{ value: previousIdCardNumber, label: previousIdCardNumber }]}
                optionRender={(option) => (
                  <>
                    <span className="sub-header-info">{t("client.attrs.previousIdentityCardNumber")}</span>
                    <br />
                    {option.value}
                  </>
                )}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <CountrySelect
            formItemProps={{
              name: "nationality",
              label: t("client.attrs.nationalityLabel"),
              rules: [validations.notNull],
              initialValue: Country.SVK,
            }}
          />
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="drivingLicenseYear"
            label={t("client.attrs.drivingLicenseYear")}
            rules={[
              birthYear ? validations.minNumber(birthYear + 17) : validations.none,
              validations.maxNumber(dayjs().year()),
            ]}
            dependencies={["birthDate"]}
          >
            <InputNumber noFormat />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item
            name="bankAccountNumber"
            label={t("client.attrs.bankAccountNumber")}
            rules={[validations.size(1, 34), validations.iban]}
            getValueProps={(value) => ({ value: value ? ibanNormalizeFunction(value) : value })}
            normalize={(value) => value?.replace(ALL_WHITE_SPACES_PATTERN, "")}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="bankSwift"
            label={t("client.attrs.bankSwift")}
            rules={[validations.size(4, 8)]}
            normalize={upperCaseStringNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="start">{t("client.titles.additionalData")}</Divider>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="verifiedByOcr" valuePropName="checked" initialValue={false} rules={[validations.none]}>
            <Checkbox>{t("client.attrs.verifiedByOcr")}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item name="verifiedByBiometry" valuePropName="checked" initialValue={false} rules={[validations.none]}>
            <Checkbox>{t("client.attrs.verifiedByBiometry")}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={colSpan + 1}>
          <Form.Item name="sanctioned" valuePropName="checked" initialValue={false} rules={[validations.none]}>
            <Checkbox>{t("client.attrs.sanctioned")}</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="involvedInCrime" valuePropName="checked" initialValue={false} rules={[validations.none]}>
            <Checkbox>{t("client.attrs.involvedInCrime")}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item name="pep" valuePropName="checked" initialValue={false} rules={[validations.none]}>
            <Checkbox>{t("client.attrs.pep")}</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 4}>
          <Form.Item
            name="note"
            label={t("client.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
